import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiX } from 'react-icons/fi';
import articleService from '../../services/articleService';

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  padding-right: 3rem;
  border: 2px solid ${props => props.theme.colors.divider};
  border-radius: ${props => props.theme.borderRadius.lg};
  font-family: ${props => props.theme.fonts.body};
  font-size: 1rem;
  background: ${props => props.theme.colors.background};
  transition: all ${props => props.theme.transitions.normal};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.1);
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textLight};
  }
`;

const SearchButton = styled.button`
  position: absolute;
  right: ${props => props.theme.spacing.sm};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  transition: all ${props => props.theme.transitions.normal};
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    background: rgba(46, 125, 50, 0.1);
  }
`;

const SearchResults = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.divider};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.lg};
  max-height: 400px;
  overflow-y: auto;
  z-index: 1000;
  margin-top: ${props => props.theme.spacing.sm};
`;

const SearchResult = styled.div`
  padding: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.divider};
  cursor: pointer;
  transition: background-color ${props => props.theme.transitions.normal};
  
  &:hover {
    background: ${props => props.theme.colors.surface};
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  .result-title {
    font-weight: 600;
    color: ${props => props.theme.colors.text};
    margin-bottom: ${props => props.theme.spacing.xs};
  }
  
  .result-meta {
    font-size: 0.8rem;
    color: ${props => props.theme.colors.textSecondary};
    display: flex;
    gap: ${props => props.theme.spacing.md};
  }
  
  .result-excerpt {
    font-size: 0.9rem;
    color: ${props => props.theme.colors.textSecondary};
    margin-top: ${props => props.theme.spacing.xs};
    line-height: 1.4;
  }
`;

const NoResults = styled.div`
  padding: ${props => props.theme.spacing.lg};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
  font-style: italic;
`;

const SearchBar = ({ placeholder = "Search articles...", onResultClick }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Recently';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const searchArticles = async () => {
      if (query.trim().length < 2) {
        setResults([]);
        setShowResults(false);
        return;
      }

      setLoading(true);
      try {
        const searchResults = await articleService.searchArticles(query);
        setResults(searchResults.slice(0, 5)); // Limit to 5 results
        setShowResults(true);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchArticles, 300);
    return () => clearTimeout(debounceTimer);
  }, [query]);

  const handleResultClick = (article) => {
    setQuery('');
    setShowResults(false);
    
    if (onResultClick) {
      onResultClick(article);
    } else {
      navigate(`/article/${article.id}`);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setShowResults(false);
      navigate(`/search?q=${encodeURIComponent(query)}`);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setShowResults(false);
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <SearchContainer ref={searchRef}>
      <form onSubmit={handleSearch}>
        <SearchInput
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length >= 2 && setShowResults(true)}
        />
        <SearchButton type="submit">
          {query ? (
            <FiX size={20} onClick={clearSearch} />
          ) : (
            <FiSearch size={20} />
          )}
        </SearchButton>
      </form>

      {showResults && (
        <SearchResults>
          {loading ? (
            <NoResults>Searching...</NoResults>
          ) : results.length > 0 ? (
            results.map((article) => (
              <SearchResult
                key={article.id}
                onClick={() => handleResultClick(article)}
              >
                <div className="result-title">{article.title}</div>
                <div className="result-meta">
                  <span>{article.section?.toUpperCase()}</span>
                  <span>By {article.author}</span>
                  <span>{formatDate(article.published_at)}</span>
                </div>
                {article.subtitle && (
                  <div className="result-excerpt">
                    {truncateText(article.subtitle)}
                  </div>
                )}
              </SearchResult>
            ))
          ) : (
            <NoResults>No articles found for "{query}"</NoResults>
          )}
        </SearchResults>
      )}
    </SearchContainer>
  );
};

export default SearchBar;